import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  fetchSignInMethodsForEmail,
  sendSignInLinkToEmail
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { actionCodeSettings, auth } from '../../api/firebase/config';
import {
  createStaffUserWhenSendemail,
  userFindByEmail,
  usersRef
} from '../../api/firebase/firestore/user';
import { clientStaffInviteSchema } from '../../api/firebase/firestore/user/validation';
import {
  blackText,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  cardContentPadding,
  cardSize,
  textMb,
  titleMb
} from '../../constants/Location';
import { role, screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomLoading from './CustomLoading';
import CustomRadioButton from './CustomRadioButton';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';

import type { RegisterScreenProps } from '../../api/firebase/auth/authType';

interface inviteStaffType {
  email: string;
  role: boolean;
}

const styles = {
  button: {
    '@media screen and (max-width:960px)': {
      height: '42px'
    }
  },
  cardContentPadding,
  cardSize,
  required: {
    color: primarycolor,
    marginLeft: '10px'
  },
  textMb,
  titleMb
};

const StaffInviteCard: React.FC<RegisterScreenProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [errorText, setErrorText] = React.useState('');
  const [firstSubmit, setFirstSubmit] = React.useState(false);
  const { currentUser } = useContext(AuthContext);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<inviteStaffType>({
    defaultValues: {
      role: true
    },
    resolver: yupResolver(clientStaffInviteSchema)
  });

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const currentUserRef = doc(usersRef, currentUser.uid);
        const currentUserSnap = await getDoc(currentUserRef);
        const currentUserRole = currentUserSnap.data()?.role as number;
        if (currentUserRole === role.member) {
          navigate('/auth/login');
        }
      }
    })();
  }, []);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('email') === undefined ||
        '' ||
        watch('role') === undefined ||
        '' ||
        Boolean(errors.email) ||
        Boolean(errors.role)
      );
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = watch('email');
      const userRef = doc(usersRef, currentUser?.uid);
      const userSnap = await getDoc(userRef);
      const companyId = userSnap.data()?.companyId as string;

      const isSignIn: string[] = await fetchSignInMethodsForEmail(auth, email);

      if (isSignIn.length) {
        setErrorText(t('register.error.email'));
        setOpen(true);
      } else {
        const user = await userFindByEmail(email);

        if (!user) {
          await createStaffUserWhenSendemail(
            props.screenType,
            email,
            watch,
            companyId
          );
        }
        await sendSignInLinkToEmail(
          auth,
          email,
          actionCodeSettings(screenType.staff, email)
        );

        if (props.screenType === screenType.client) {
          navigate('/client/mypage/staff/invite/complete');
        } else if (props.screenType === screenType.agent) {
          navigate('/agent/mypage/staff/invite/complete');
        } else {
          navigate('/publisher/mypage/staff/invite/complete');
        }
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12}>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: blackText }]}
                component="div"
              >
                {t('staff.invite.title')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText={t('staff.edit.email')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder="xxx@en-broker.co.jp"
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }) => (
                  <CustomRadioButton
                    {...field}
                    error={Boolean(errors.role)}
                    helperText={errors.role?.message}
                    labelText={t('staff.edit.role')}
                    requiredText={t('form.required')}
                    isRequired
                    trueOption={t('staff.edit.admin')}
                    falseOption={t('staff.edit.member')}
                    guideText={`${t('staff.edit.roleGuide.admin')}\n\n${t(
                      'staff.edit.roleGuide.member'
                    )}`}
                  />
                )}
                control={control}
                name="role"
              />
              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                fullWidth
                sx={[primaryButtonBackground, styles.button]}
                onClick={() => {
                  void (async () => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => await onSubmit())();
                  })();
                }}
              >
                <Typography component="div" color="white" sx={{ fontSize: 15 }}>
                  {t('staff.invite.button')}
                </Typography>
              </Button>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default StaffInviteCard;
